import React, { useState } from "react";
import { Bell, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const PLACEHOLDERS = [
  { key: "{{from_name}}", label: "Visitor Name" },
  { key: "{{from_email}}", label: "Visitor Email" },
  { key: "{{company}}", label: "Company" },
  { key: "{{phone}}", label: "Phone" },
  { key: "{{service}}", label: "Service" },
  { key: "{{message}}", label: "Message" },
  { key: "{{submission_date}}", label: "Date" },
  { key: "{{reference_id}}", label: "Reference ID" },
  { key: "{{to_email}}", label: "Recipient" },
];

export default function InternalEmailSettings({ settings, onSave }) {
  const [cfg, setCfg] = useState({ ...settings });

  const update = (key, value) => setCfg((prev) => ({ ...prev, [key]: value }));
  const handleSave = () => onSave(cfg);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
            <Bell className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Internal Notification Email</h2>
            <p className="text-sm text-slate-500">Alert sent to your company team for follow-up</p>
          </div>
        </div>

        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl mb-5">
          <div>
            <Label className="text-slate-700 font-medium">Enable Internal Notification</Label>
            <p className="text-xs text-slate-500 mt-1">Turns internal alert email on/off</p>
          </div>
          <Switch data-testid="internal-enabled-toggle" checked={cfg.internalEmailEnabled !== false} onCheckedChange={(v) => update("internalEmailEnabled", v)} />
        </div>

        <div className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="text-slate-700">To Email (Primary Recipient) *</Label>
              <Input data-testid="internal-to-email" value={cfg.internalToEmail || ""} onChange={(e) => update("internalToEmail", e.target.value)} placeholder="sales@yourdomain.com" className="bg-slate-50 border-slate-200" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700">Additional Recipients</Label>
              <Input data-testid="internal-additional" value={cfg.additionalRecipients || ""} onChange={(e) => update("additionalRecipients", e.target.value)} placeholder="info@, support@ (comma-separated)" className="bg-slate-50 border-slate-200" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="text-slate-700">CC Email</Label>
              <Input data-testid="internal-cc" value={cfg.ccEmail || ""} onChange={(e) => update("ccEmail", e.target.value)} placeholder="manager@yourdomain.com" className="bg-slate-50 border-slate-200" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700">BCC Email</Label>
              <Input data-testid="internal-bcc" value={cfg.bccEmail || ""} onChange={(e) => update("bccEmail", e.target.value)} placeholder="admin@yourdomain.com" className="bg-slate-50 border-slate-200" />
            </div>
          </div>
        </div>
      </div>

      {/* Template Placeholders Reference */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">Template Placeholders</h3>
        <p className="text-sm text-slate-500 mb-4">Use these in your EmailJS internal notification template:</p>
        <div className="flex flex-wrap gap-2">
          {PLACEHOLDERS.map((p) => (
            <span key={p.key} className="px-3 py-1.5 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-md font-mono">
              {p.key} <span className="text-amber-400 font-sans">— {p.label}</span>
            </span>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button data-testid="save-internal-btn" onClick={handleSave} className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold px-8">
          Save Internal Email Settings
        </Button>
      </div>
    </div>
  );
}
